import { ref } from "vue";
import { defineStore } from "pinia";
import AuthService, { type AuthenticateResult } from "@/core/services/AuthService";
import AuthTokenStore from "@/core/localStorage/AuthTokenLS";
import AppStorage from "@/core/localStorage/AppStorageLS";
import { resetAllStores } from "@/stores/reset";
type Nullable<T> = T | null;

// Which SkyFlok User roles are allowed to log in
const allowed_roles = ["S3_DEV", 'OA']

export interface LoginCredentials {
  email: string;
  password: string;
  mfa_code: Nullable<string>;
}

export interface PasswordResetRequest {
  email_address: string;
}

export interface PasswordResetValidateRequest {
  token: string;
}

export interface PasswordResetConfirmRequest {
  token: string;
  new_password: string;
}

export interface LoginError {
  // i18n key
  key: string
  // i18n string params
  params?: any
}

export const useAuthStore = defineStore("auth", {
  state: () => {
  const isAuthenticated = ref<boolean>(AuthTokenStore.getToken() !== null);

  function purgeAuth() {
    isAuthenticated.value = false
    AuthTokenStore.destroyToken();
    if (AuthService.axios?.defaults?.headers?.common) {
      delete AuthService.axios.defaults.headers.common['Authorization']
    }
  }




  /**
   * Logs in the user with the given credentials and checks they have
   * a Role that allows access to this Developer Portal.
   *
   * If the login is successful (`null` is returned), the access token is
   * saved persistently via AuthTokenStore.
   *
   * @param credentials
   * @returns Error message if there was a problem, or null if login succeeded.
   */
  async function login(credentials: LoginCredentials) : Promise<LoginError | null> {

    let accessToken: Nullable<string> = null
    const login_err = await AuthService.login(credentials.email.trim(), credentials.password)
      .then(({ data }) => {
        // The whole response data is the plaintext token (not a JSON object)
        accessToken = data
        return null
      })
      .catch(({ response }) => {
        purgeAuth()
        switch(response.status){
          case 401: return {key:'auth.errors.unauthorized'}
          case 403: return {key: 'auth.errors.wrong_mfa_code'}
          case 404: return {key: 'auth.errors.user_not_found'}
          case 429: return {key: 'auth.errors.mfa_rate_limit', params: {seconds: response.data.seconds_to_wait}}
          case 500: return {key: 'errors.server_error'}
          default: return {key: 'errors.generic'}
        }
      });
    if(login_err || !accessToken) { return login_err }

    // Verify token and load auth claims.
    const auth = await verifyToken(accessToken)
    if(!auth) {
      // Should not fail after a fresh login
      // TODO report issue to FrontendSupport
      return {key: 'errors.server_error'}
    }

    // Check that the user has any of the allowed roles
    if (!_is_user_role_allowed(auth)){
      purgeAuth()
      return {key:'auth.errors.unauthorized'}
    }
    // Auth successful, save the token
    AuthTokenStore.saveToken(accessToken)
    return null
  }

  /**
   * Logs out locally and invalidates the token on the server.
   *
   * @param clearStorage Also remove every key the app keeps in localStorage
   *   (theme, language, cached profile, ...). Set when the user signs out;
   *   internal resets (e.g. visiting a protected page without a session) keep them.
   */
  function logout({ clearStorage = false }: { clearStorage?: boolean } = {}) {
    const currentToken = AuthTokenStore.getToken()

    purgeAuth();
    resetAllStores();
    if (clearStorage) {
      AppStorage.clearAppStorage();
    }

    if (!currentToken) {
      return
    }

    // Best-effort logout call to invalidate the token. Auth is already purged
    // locally, so a 401 from this request must not restart logout handling.
    AuthService.logout(currentToken).catch(_ => {})
  }


  // Verify that the stored token is still valid.
  // Sets `isAuthenticated`
  async function verifyAuth() : Promise<void> {
    const accessToken = AuthTokenStore.getToken()
    if(!accessToken){
      purgeAuth()
      return
    }
    const auth = await verifyToken(accessToken)
    if (auth && !_is_user_role_allowed(auth)) {
      purgeAuth()
    }
  }

  async function verifyToken(accessToken: string) : Promise<AuthenticateResult | null> {
    return AuthService.verify_token(accessToken)
      .then(({data}) => {
        isAuthenticated.value = true
        return data
      })
      .catch(_ => {
        purgeAuth()
        return null
      });
  }

  // Check if the authenticated user is allowed to use this webapp
  function _is_user_role_allowed(auth: AuthenticateResult) : boolean {
    for(let i=0 ; i<allowed_roles.length ; ++i){
      if(auth.roles.includes(allowed_roles[i])) return true
    }
    return false
  }

  function _password_reset_error(response: any) : LoginError {
    switch(response?.status){
      case 400: return {key: 'auth.errors.password_reset_invalid'}
      case 500: return {key: 'errors.server_error'}
      default: return {key: 'errors.generic'}
    }
  }

  async function requestPasswordReset(payload: PasswordResetRequest) : Promise<LoginError | null> {
    return AuthService.request_password_reset(payload.email_address.trim())
      .then(_ => null)
      .catch(error => _password_reset_error(error?.response));
  }

  async function validatePasswordReset(payload: PasswordResetValidateRequest) : Promise<LoginError | null> {
    return AuthService.validate_password_reset(payload.token)
      .then(({ data }) => data?.valid === true ? null : {key: 'auth.errors.password_reset_invalid'})
      .catch(error => _password_reset_error(error?.response));
  }

  async function confirmPasswordReset(payload: PasswordResetConfirmRequest) : Promise<LoginError | null> {
    return AuthService.confirm_password_reset(payload.token, payload.new_password)
      .then(_ => null)
      .catch(error => _password_reset_error(error?.response));
  }

  function forgotPassword(email: string) : Promise<LoginError | null> {
    return requestPasswordReset({ email_address: email.trim() })
  }

  return {
    isAuthenticated,
    login,
    logout,
    purgeAuth,
    verifyAuth,
    requestPasswordReset,
    validatePasswordReset,
    confirmPasswordReset,
    forgotPassword,
  }
}
});
